// showToast.tsx
import toast from "react-hot-toast";
import { CheckCircle, XCircle, AlertTriangle, Info, X, Check, RotateCcw } from "lucide-react";
import { useState, useEffect } from "react";

interface ShowToastOptions {
  title: string;
  description?: string;
  type?: "success" | "error" | "warning" | "info";
  actionLabel?: string;
  onAction?: () => void;
  duration?: number;
  showCheckbox?: boolean;
  checkboxKey?: string;
}

const toastConfig = {
  success: {
    icon: CheckCircle,
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    textAccent: "text-emerald-700",
    progressBg: "bg-gradient-to-r from-emerald-400 to-emerald-600",
  },
  error: {
    icon: XCircle,
    bg: "bg-red-50",
    border: "border-red-200",
    textAccent: "text-red-700",
    progressBg: "bg-gradient-to-r from-red-400 to-red-600",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    textAccent: "text-amber-700",
    progressBg: "bg-gradient-to-r from-amber-400 to-amber-600",
  },
  info: {
    icon: Info,
    bg: "bg-white",
    border: "border-primary/30",
    textAccent: "text-primary",
    progressBg: "bg-gradient-to-r from-primary to-secondary",
  },
};

// Affiche un petit toast d'annulation temporaire
const showUndoToast = (onUndo: () => void, checkboxKey: string) => {
  const undoId = toast.custom(
    (t) => (
      <div
        className={`
          z-[10000] w-full max-w-sm
          transform transition-all duration-300 ease-out
          ${t.visible ? "translate-x-0 opacity-100 scale-100" : "translate-x-full opacity-0 scale-95"}
          bg-white rounded-xl shadow-xl border border-primary/30 p-3
        `}
      >
        <div className="flex items-center gap-3">
          <RotateCcw size={16} className="text-primary" />
          <span className="text-sm text-gray-700">Rappel désactivé pour ce message</span>
          <button
            onClick={() => {
              onUndo();
              toast.dismiss(undoId);
            }}
            className="ml-auto text-xs font-medium text-primary hover:underline"
          >
            Annuler
          </button>
        </div>
      </div>
    ),
    { duration: 5000, position: "top-right" }
  );
  return undoId;
};

const ToastContent = ({
  t,
  title,
  description,
  actionLabel,
  onAction,
  duration,
  showCheckbox,
  checkboxKey,
  config,
  Icon,
}: any) => {
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(duration / 1000));
  const [isChecked, setIsChecked] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => toast.dismiss(t.id), 300);
  };

  useEffect(() => {
    if (secondsLeft <= 0) {
      handleClose();
      return;
    }
    const interval = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsChecked(checked);

    if (checked && checkboxKey) {
      // Sauvegarde immédiate
      localStorage.setItem(checkboxKey, "true");
      // Affiche un toast "Annuler"
      showUndoToast(() => {
        // Annulation : supprimer la préférence et décoché la case
        localStorage.removeItem(checkboxKey);
        setIsChecked(false);
        toast.success("Rappel réactivé");
      }, checkboxKey);
    } else if (!checked && checkboxKey) {
      // Si l'utilisateur a décoché (peu probable, mais au cas où)
      localStorage.removeItem(checkboxKey);
    }
  };

  return (
    <div
      className={`
        relative z-[9999] w-full max-w-md
        transform transition-all duration-300 ease-out
        ${t.visible && !isExiting ? "translate-x-0 opacity-100 scale-100" : "translate-x-full opacity-0 scale-95"}
      `}
    >
      <div className={`rounded-2xl shadow-2xl overflow-hidden ${config.bg} border ${config.border}`}>
        {/* Barre de progression */}
        <div className="relative h-1.5 w-full bg-gray-200/80">
          <div
            className={`absolute inset-0 ${config.progressBg} rounded-r-full shadow-sm`}
            style={{
              width: `${(secondsLeft / (duration / 1000)) * 100}%`,
              transition: "width 1s linear",
            }}
          />
          <div className="absolute right-2 -top-6 flex items-center gap-1 bg-white/80 backdrop-blur-sm rounded-full px-2 py-0.5 shadow-xs">
            <span className="text-[10px] font-mono font-semibold text-gray-600">{secondsLeft}s</span>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <Icon size={22} className={config.textAccent} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className={`font-semibold ${config.textAccent}`}>{title}</h4>
                <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                  <X size={16} />
                </button>
              </div>
              {description && (
                <p className="text-sm text-gray-700 mt-1 leading-relaxed">{description}</p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                {actionLabel && (
                  <button
                    onClick={() => {
                      onAction?.();
                      handleClose();
                    }}
                    className={`
                      px-4 py-1.5 rounded-full text-xs font-medium
                      transition-all duration-200
                      ${config.textAccent} bg-white/80 hover:bg-white
                      shadow-sm hover:shadow backdrop-blur-sm
                    `}
                  >
                    {actionLabel}
                  </button>
                )}
                {showCheckbox && checkboxKey && (
                  <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={handleCheckboxChange}
                        className="sr-only"
                      />
                      <div
                        className={`
                          w-4 h-4 rounded border-2 flex items-center justify-center
                          transition-all duration-200
                          ${isChecked
                            ? "bg-primary border-primary shadow-sm"
                            : "border-gray-300 bg-white group-hover:border-primary/60"
                          }
                        `}
                      >
                        {isChecked && <Check size={10} className="text-white" />}
                      </div>
                    </div>
                    <span className="group-hover:text-gray-800">Ne plus me rappeler</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const showToast = ({
  title,
  description,
  type = "info",
  actionLabel,
  onAction,
  duration = 5000,
  showCheckbox = false,
  checkboxKey,
}: ShowToastOptions) => {
  // Vérifier si déjà masqué
  const isChecked = checkboxKey ? localStorage.getItem(checkboxKey) === "true" : false;
  if (isChecked) return;

  const config = toastConfig[type];
  const Icon = config.icon;

  toast.custom(
    (t) => (
      <ToastContent
        t={t}
        title={title}
        description={description}
        actionLabel={actionLabel}
        onAction={onAction}
        duration={duration}
        showCheckbox={showCheckbox}
        checkboxKey={checkboxKey}
        config={config}
        Icon={Icon}
      />
    ),
    {
      duration,
      position: "top-right",
    }
  );
};